import React, { useState, useEffect, useCallback } from 'react';
import { isMobile } from 'react-device-detect';
import clsx from 'clsx';
import { Button, Popover, Icons, IButtonProps } from 'components';
import { IState } from 'services/root-reducer';

import { MeasurementToolsSwitcher } from './measurement-tools-switcher';

import './style.scss';


export interface IMeasurementsControl {
    className?: string;
    view: IState['omjs']['omjsViewInstance'];
}

enum EMeasurementMode {
    OFF,
    DRAW,
    IDLE
}

export const MeasurementsControl: React.FC<IMeasurementsControl> = (props): JSX.Element => {
    const { className = '', view } = props;

    const [ referenceElement, setReferenceElement ] = useState<HTMLElement | null>(null);
    const [ showPopover, setShowPopover ] = useState(false);
    const [ mode, setMode ] = useState<EMeasurementMode>(EMeasurementMode.OFF);
    const [ currInteraction, setCurrInteraction ] = useState<string | null>(null);

    const handleDrawEnd = useCallback((event) => {
        setMode(EMeasurementMode.IDLE);
        view.getMeasurement().activateModifyInteraction(event.feature);
    }, []);

    useEffect(() => {
        const measurement = view.getMeasurement();
        measurement.on('drawend', handleDrawEnd);
        return () => {
            measurement.off('drawend', handleDrawEnd);
        };
    }, []);

    const switchMeasurement = (value: string) => {
        view.getMeasurement().changeInteraction(value);
        if (value === currInteraction && mode === EMeasurementMode.DRAW) {
            setMode(EMeasurementMode.IDLE);
            setCurrInteraction(null);
            view.getMeasurement().stopDrawInteraction();
        } else {
            setMode(EMeasurementMode.DRAW);
            setCurrInteraction(value);
            view.getMeasurement().deactivateModifyInteraction(true);
        }
    };

    const switchOff = () => {
        if (mode !== EMeasurementMode.OFF) {
            const measurement = view.getMeasurement();
            measurement.stopInteraction();
            setMode(EMeasurementMode.OFF);
            setCurrInteraction(null);
        }
    };

    const renderIcon = () => {
        let icon;
        switch (currInteraction) {
            case 'Point': icon = <Icons.IconPoint />; break;
            case 'distance': icon = <Icons.IconDistance />; break;
            case 'area': icon = <Icons.IconArea />; break;
            case 'Circle': icon = <Icons.IconRadius />; break;
            default: icon = <Icons.IconRuler />; break;
        }
        return icon;
    };

    /**
     * Костыль для фикса задачи `iPad app. Кнопка расстояния иногда может не открыться, когда создаёшь несколько расстояний`
     * "Правильное" решение этой баги занимало слишком много времени, давало мало результатов и скорее всего для решения этой
     * баги проще вообще было бы переписать полностью компонент `Popover`.
     *
     * В виду устройства работы компонента Popover (как я понял ему все равно на то какое значение `open` ему передали потому что у него внутри
     * своя логика скрытия попапа по таймауту) в обработчике `onClick` может случится баг при котором showPopover (флаг **в этом компоненте**,
     * который показывает открыт ли попап) будет равен true, сам поповер показываться НЕ будет (потому что `Popover` уже скрыл попап и выставил
     * флаг **у себя в компоненте**, что попап скрыт), и рисование будет выключено.
     * Скрипт выполняет проверку `if (mode === EMeasurementMode.OFF) setShowPopover(true);`,
     * т.к. showPopover уже и так выставлен в true, обновление компонента не происходит и выпадашка больше не показывается.
     */
    let buttonInteractions: Partial<IButtonProps> = {};
    if (!isMobile) {
        /* Это старая логика, которая работает на десктопах, но не работает на планшетах */
        buttonInteractions['onClick'] = () => {
            if (mode === EMeasurementMode.OFF) {
                setShowPopover(true);
            } else {
                switchOff();
            }
        };
        buttonInteractions['onMouseEnter'] = () => setShowPopover(true);
        buttonInteractions['onMouseLeave'] = () => setShowPopover(false);
    } else {
        /* А это новая логика, которая работает на планшетах, но не работает на десктопах */
        buttonInteractions['onClick'] = () => {
            if (!showPopover && mode === EMeasurementMode.OFF) {
                setShowPopover(true);
            } else {
                switchOff();
                setShowPopover(false);
            }
        };
        buttonInteractions['onMouseLeave'] = () => setShowPopover(false);
    }

    return (
        <React.Fragment>
            <Button
                {...buttonInteractions}
                className={clsx({ 'oms-measurements__btn-active': mode !== EMeasurementMode.OFF })}
                ref={setReferenceElement}
                icon={renderIcon()}
                variant='black'
            />
            <Popover
                hideTimeout={300}
                showTimeout={300}
                targetElement={referenceElement}
                offset={[ 0, 4 ]}
                placement="left-start"
                open={showPopover}
            >
                <MeasurementToolsSwitcher switchMeasurement={switchMeasurement} activeTool={currInteraction} />
            </Popover>
        </React.Fragment>
    );
};
